import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

export type NavbarProps = {
  brand?: ReactNode;
  children: ReactNode;
  sticky?: boolean;
  className?: string;
  actions?: ReactNode;
  appearance?: 'solid' | 'blur' | 'transparent';
  border?: 'none' | 'bottom' | 'top' | 'both';
};

const appearanceClasses = {
  solid: 'bg-background shadow-sm',
  blur: 'bg-background/70 backdrop-blur-md supports-[backdrop-filter]:bg-background/60 border border-border/60',
  transparent: 'bg-transparent',
} as const;

const borderClasses = {
  none: '',
  bottom: 'border-b border-border',
  top: 'border-t border-border',
  both: 'border-y border-border',
} as const;

function Navbar({
  brand,
  children,
  sticky = true,
  className,
  actions,
  appearance = 'solid',
  border = 'none',
}: Readonly<NavbarProps>) {
  return (
    <nav
      data-appearance={appearance}
      className={cn(
        sticky && 'sticky top-0 z-50',
        appearanceClasses[appearance],
        borderClasses[border],
        className
      )}
    >
      <div className="mx-2 w-auto max-w-full">
        <div className="flex h-14 items-center justify-between px-0">
          {brand && <div className="shrink-0">{brand}</div>}
          <div className="ml-2 flex flex-1 items-center space-x-6 overflow-hidden md:ml-10">
            {children}
          </div>
          {actions && <div className="flex items-center gap-1">{actions}</div>}
        </div>
      </div>
    </nav>
  );
}

Navbar.displayName = 'Navbar';

export { Navbar };
export default Navbar;
